/**
 * useIntervalManager Hook
 *
 * Hook para gerenciar automaticamente a troca de intervalos de pedidos.
 * Verifica o intervalo atual a cada 60 segundos e dispara callbacks quando houver mudanças.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useCurrentIntervals } from '@queries/order';
import { toast } from 'sonner';
import type { Interval, IntervalResponse, CurrentIntervalResponse } from '@services/api/orderTypes';
import { FILA_CONFIG_STORAGE_KEY } from '../types/config';
import type { FilaConfig } from '../types/config';

interface UseIntervalManagerParams {
  establishmentId: string;
  enabled?: boolean;
  onIntervalChange?: (newInterval: Interval, oldInterval?: Interval) => void;
  onError?: (error: Error) => void;
}

interface UseIntervalManagerReturn {
  currentInterval: Interval | null;
  isChecking: boolean;
  error: Error | null;
  lastChecked: Date | null;
  forceCheck: () => void;
  restart: () => void;
}

const CHECK_INTERVAL = 10 * 1000; // 60 segundos conforme especificado
const RETRY_DELAY = 5 * 1000; // 5 segundos para retry em caso de erro
const MAX_RETRY_ATTEMPTS = 3; // Máximo de tentativas de retry

// Mutex para evitar salvamento concorrente no localStorage
let isSavingToStorage = false;

/**
 * Salva IMEDIATAMENTE os dados da resposta da API no localStorage
 * Esta função é chamada assim que a resposta chega, antes de qualquer processamento
 */
const saveImmediatelyFromAPIResponse = (apiResponse: any, establishmentId: string) => {
  try {
    console.log('🚀 saveImmediatelyFromAPIResponse called:', {
      apiResponse,
      establishmentId,
      responseKeys: apiResponse ? Object.keys(apiResponse) : 'null',
    });

    // Verificar se temos os campos esperados no formato CurrentIntervalResponse
    if (apiResponse && typeof apiResponse === 'object' && 'interval_id' in apiResponse && 'name' in apiResponse) {

      // Carregar configuração atual do localStorage
      const storedConfig = localStorage.getItem(FILA_CONFIG_STORAGE_KEY);
      if (!storedConfig) {
        console.warn('⚠️ saveImmediatelyFromAPIResponse: Nenhuma configuração encontrada no localStorage');
        return;
      }

      const config: FilaConfig = JSON.parse(storedConfig);
      console.log('📋 Config atual antes do salvamento:', {
        autoIntervalChange: config.autoIntervalChange,
        currentInterval: config.currentInterval,
      });

      // Apenas salvar se autoIntervalChange estiver ativo
      if (!config.autoIntervalChange) {
        console.log('📝 saveImmediatelyFromAPIResponse: autoIntervalChange está inativo, não salvando');
        return;
      }

      // Criar objeto Interval a partir da resposta da API
      const intervalFromAPI: Interval = {
        id: apiResponse.interval_id,
        name: apiResponse.name,
        start_time: apiResponse.start_time || '',
        end_time: apiResponse.end_time || '',
        is_active: apiResponse.is_default || true,
        establishment_id: establishmentId,
        order_count: apiResponse.order_count || 0,
      };

      console.log('🎯 Intervalo criado da API response:', intervalFromAPI);

      // Atualizar APENAS os campos necessários, preservando tudo o resto
      const updatedConfig: FilaConfig = {
        ...config,
        currentInterval: {
          id: intervalFromAPI.id,
          name: intervalFromAPI.name,
          startTime: intervalFromAPI.start_time,
          endTime: intervalFromAPI.end_time,
          isActive: intervalFromAPI.is_active,
          establishmentId: intervalFromAPI.establishment_id || establishmentId,
          orderCount: intervalFromAPI.order_count,
        },
        selectedIntervalName: intervalFromAPI.name,
      };

      // Salvar no localStorage
      localStorage.setItem(FILA_CONFIG_STORAGE_KEY, JSON.stringify(updatedConfig));

      // Verificar se foi salvo
      const savedConfig = localStorage.getItem(FILA_CONFIG_STORAGE_KEY);
      const parsedSaved = savedConfig ? JSON.parse(savedConfig) : null;

      console.log('💾 SALVAMENTO IMEDIATO concluído:', {
        success: !!parsedSaved,
        intervalId: parsedSaved?.currentInterval?.id,
        intervalName: parsedSaved?.currentInterval?.name,
        autoIntervalChange: parsedSaved?.autoIntervalChange,
        originalResponse: apiResponse,
      });

      // Disparar evento para notificar outras abas
      window.dispatchEvent(new CustomEvent('filaConfigChanged', {
        detail: {
          type: 'interval-immediately-updated',
          interval: updatedConfig.currentInterval,
          timestamp: new Date().toISOString()
        }
      }));

    } else {
      console.warn('⚠️ saveImmediatelyFromAPIResponse: Formato de resposta inválido ou campos ausentes');
    }

  } catch (error) {
    console.error('❌ Erro no salvamento imediato:', error);
  }
};

/**
 * Salva os dados do intervalo atual no localStorage quando autoIntervalChange está ativo
 */
const saveCurrentIntervalToConfig = (interval: Interval, establishmentId: string) => {
  try {
    // Carregar configuração atual do localStorage
    const storedConfig = localStorage.getItem(FILA_CONFIG_STORAGE_KEY);

    if (!storedConfig) {
      console.warn('⚠️ saveCurrentIntervalToConfig: Nenhuma configuração encontrada no localStorage');
      return;
    }

    const config: FilaConfig = JSON.parse(storedConfig);

    // Apenas salvar se autoIntervalChange estiver ativo
    if (!config.autoIntervalChange) {
      console.log('📝 saveCurrentIntervalToConfig: autoIntervalChange está inativo, não salvando no localStorage');
      return;
    }

    // Evitar salvamento concorrente
    if (isSavingToStorage) {
      console.log('⏸️ saveCurrentIntervalToConfig: já está salvando, ignorando chamada concorrente');
      return;
    }

    // Atualizar configuração com dados completos do intervalo atual
    // Preservar EXATAMENTE todos os campos existentes, exceto os que vamos atualizar
    const updatedConfig: FilaConfig = {
      ...config, // Manter TODOS os campos existentes inalterados
      // Apenas atualizar campos específicos do intervalo
      currentInterval: {
        id: interval.id,
        name: interval.name,
        startTime: interval.start_time,
        endTime: interval.end_time,
        isActive: interval.is_active,
        establishmentId: interval.establishment_id || establishmentId,
        orderCount: interval.order_count,
      },
      // Também atualizar selectedIntervalName para compatibilidade (mas sem mudar selectedInterval)
      selectedIntervalName: interval.name,
      // NÃO mexer no selectedInterval para não causar conflitos com configurações manuais
    };

    // Não fazer nada - dados já foram salvos pela saveImmediatelyFromAPIResponse
  } catch (error) {
    console.error('❌ Erro ao acessar saveCurrentIntervalToConfig:', error);
  }
};

export function useIntervalManager({
  establishmentId,
  enabled = true,
  onIntervalChange,
  onError,
}: UseIntervalManagerParams): UseIntervalManagerReturn {
  // Estado local
  const [currentInterval, setCurrentInterval] = useState<Interval | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  // Refs para controle do timer e estado
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const lastIntervalIdRef = useRef<string | null>(null);
  const checkingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastCheckTimeRef = useRef<number>(0); // Prevenir múltiplas requisições muito rápidas

  // Query do React Query para obter intervalos atuais
  const { data: intervalsData, isLoading: isQueryLoading, error: queryError, refetch } = useCurrentIntervals(establishmentId);

  // Log para verificar se a query está sendo chamada
  console.log('🔍 useCurrentIntervals status:', {
    establishmentId,
    isLoading: isQueryLoading,
    hasData: !!intervalsData,
    hasError: !!queryError,
    dataKeys: intervalsData ? Object.keys(intervalsData) : 'null',
  });

  /**
   * Processa os dados de intervalos e detecta mudanças
   */
  const processIntervalData = useCallback(
    (
      data:
        | IntervalResponse
        | CurrentIntervalResponse
        | { interval_id: string; name: string; start_time?: string; end_time?: string; is_default?: boolean; resolved_at?: string },
    ) => {
      if (!isMountedRef.current) return;

      console.log('🔄 Processing interval data:', {
        establishmentId,
        data,
        currentIntervalId: currentInterval?.id,
        lastIntervalId: lastIntervalIdRef.current,
        dataType: typeof data,
        dataKeys: data ? Object.keys(data) : 'null',
      });

      setError(null);
      setRetryCount(0);
      setLastChecked(new Date());

      // Processar resposta real da API conforme documentação
      // O endpoint /orders/intervals/current deve retornar o intervalo ativo atual
      let newInterval: Interval | null = null;

      // Type guards para verificar o tipo de resposta
      const isCurrentIntervalResponse = (d: any): d is CurrentIntervalResponse => {
        return d && typeof d === 'object' && 'interval_id' in d && 'name' in d;
      };

      const isIntervalResponse = (d: any): d is IntervalResponse => {
        return d && typeof d === 'object' && ('intervals' in d || 'current_interval' in d);
      };

      const isInterval = (d: any): d is Interval => {
        return d && typeof d === 'object' && 'id' in d && 'name' in d && 'is_active' in d;
      };

      // Caso 1: Formato esperado do endpoint /orders/intervals/current (CurrentIntervalResponse)
      if (isCurrentIntervalResponse(data)) {
        newInterval = {
          id: data.interval_id,
          name: data.name,
          start_time: data.start_time || '',
          end_time: data.end_time || '',
          is_active: data.is_default || true, // Se está vindo no current, está ativo (ou é default)
          establishment_id: establishmentId,
          order_count: data.order_count || 0,
        };

        }
      // Caso 2: Formato com current_interval (IntervalResponse aninhada)
      else if (isIntervalResponse(data) && data.current_interval) {
        newInterval = data.current_interval;
      }
      // Caso 3: Lista de intervalos - pegar o primeiro que está ativo (IntervalResponse)
      else if (isIntervalResponse(data) && data.intervals && Array.isArray(data.intervals)) {
        // Procurar por intervalo ativo primeiro
        const activeInterval = data.intervals.find((interval: any) => interval.is_active);
        // Se não encontrar ativo, pegar o primeiro
        const selectedInterval = activeInterval || data.intervals[0];

        if (selectedInterval) {
          newInterval = {
            id: selectedInterval.id,
            name: selectedInterval.name,
            start_time: selectedInterval.start_time || '',
            end_time: selectedInterval.end_time || '',
            is_active: selectedInterval.is_active || false,
            establishment_id: selectedInterval.establishment_id || establishmentId,
            order_count: selectedInterval.order_count || 0,
          };
        }
      }
      // Caso 4: Dados diretos no formato Interval (resposta já processada)
      else if (isInterval(data)) {
        newInterval = data;
      }

      // Validar intervalo encontrado
      if (newInterval && newInterval.id) {
        console.log('✅ Interval processed successfully:', {
          intervalId: newInterval.id,
          intervalName: newInterval.name,
          isActive: newInterval.is_active,
          establishmentId: newInterval.establishment_id,
          startTime: newInterval.start_time,
          endTime: newInterval.end_time,
          orderCount: newInterval.order_count,
        });

        if (newInterval.id !== lastIntervalIdRef.current) {
          console.log('🔔 Interval change detected:', {
            oldIntervalId: lastIntervalIdRef.current,
            newIntervalId: newInterval.id,
            newIntervalName: newInterval.name,
          });

          const oldInterval = currentInterval;
          setCurrentInterval(newInterval);
          lastIntervalIdRef.current = newInterval.id;

          // Salvar dados do intervalo atual no localStorage quando autoIntervalChange está ativo
          saveCurrentIntervalToConfig(newInterval, establishmentId);

          // Disparar callback de mudança de intervalo
          if (onIntervalChange) {
            try {
              onIntervalChange(newInterval, oldInterval || undefined);
            } catch (error) {
              console.error('❌ Error in onIntervalChange callback:', error);
            }
          }

          // Mostrar notificação para o usuário
          toast.success(`Intervalo alterado: ${newInterval.name}`, {
            description: 'Os pedidos foram atualizados automaticamente.',
            duration: 4000,
          });
        } else if (!currentInterval) {
          // Primeira carga de intervalo
          console.log('🎯 First interval loaded:', {
            intervalId: newInterval.id,
            intervalName: newInterval.name,
          });
          setCurrentInterval(newInterval);
          lastIntervalIdRef.current = newInterval.id;

          // Salvar dados do intervalo atual no localStorage quando autoIntervalChange está ativo
          saveCurrentIntervalToConfig(newInterval, establishmentId);
        }
      } else {
        // Nenhum intervalo encontrado
        console.warn('⚠️ No valid interval found in response:', {
          data,
          establishmentId,
        });

        if (currentInterval) {
          // Limpar intervalo atual se não houver mais
          console.log('🧹 Clearing current interval - no active interval found');
          setCurrentInterval(null);
          lastIntervalIdRef.current = null;
        }
      }
    },
    [currentInterval?.id, establishmentId, onIntervalChange],
  );

  /**
   * Função para verificar o intervalo atual
   */
  const checkInterval = useCallback(async () => {
    const now = Date.now();

    if (!enabled || !establishmentId) {
      console.log('🚫 Skipping interval check:', { enabled, establishmentId, reason: 'disabled or no establishmentId' });
      return;
    }

    // Prevenir múltiplas requisições muito rápidas (mínimo 2 segundos entre verificações)
    if (now - lastCheckTimeRef.current < 2000) {
      console.log('⏸️ Throttling check - too soon since last request:', {
        timeSinceLastCheck: now - lastCheckTimeRef.current,
        minimumInterval: 2000,
        timestamp: new Date().toISOString(),
      });
      return;
    }

    // Se já está verificando, deixar o React Query gerenciar
    if (isQueryLoading) {
      console.log('⏳ Query already in progress, skipping manual check:', {
        establishmentId,
        timestamp: new Date().toISOString(),
      });
      return;
    }

    // Atualizar timestamp da última verificação
    lastCheckTimeRef.current = now;

    console.log('🔍 Checking interval:', {
      establishmentId,
      currentIntervalId: currentInterval?.id,
      timestamp: new Date().toISOString(),
    });

    setIsChecking(true);
    setError(null);

    // Timeout de segurança para garantir que isChecking não fique preso
    if (checkingTimeoutRef.current) {
      clearTimeout(checkingTimeoutRef.current);
    }
    checkingTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        console.log('⏰ Safety timeout - resetting isChecking flag');
        setIsChecking(false);
      }
    }, 15000); // 15 segundos de timeout máximo

    try {
      const requestStartTime = Date.now();
      console.log('🚀 Making API request to /orders/intervals/current', {
        establishmentId,
        timestamp: new Date().toISOString(),
      });

      const result = await refetch();
      const requestDuration = Date.now() - requestStartTime;

      console.log('✅ API request completed:', {
        success: !!result.data,
        data: result.data,
        requestDuration: `${requestDuration}ms`,
        timestamp: new Date().toISOString(),
      });

      // Reset retry count e timestamp em caso de sucesso
      setRetryCount(0);
      lastCheckTimeRef.current = Date.now(); // Atualizar timestamp em caso de sucesso
    } catch (error) {
      console.error('❌ Error checking interval:', error);

      const errorObj = error instanceof Error ? error : new Error('Erro ao verificar intervalo');
      setError(errorObj);

      // Reset do isChecking imediatamente em caso de erro
      setIsChecking(false);

      // Disparar callback de erro (com proteção)
      if (onError && isMountedRef.current) {
        try {
          onError(errorObj);
        } catch (callbackError) {
          console.error('❌ Error in onError callback:', callbackError);
        }
      }

      // Mostrar notificação de erro
      toast.error('Erro ao verificar intervalo', {
        description: retryCount < MAX_RETRY_ATTEMPTS ? 'Tentando novamente...' : 'Verifique sua conexão.',
        duration: 3000,
      });

      // Incrementar retry count apenas se não excedeu o máximo
      const newRetryCount = retryCount + 1;
      setRetryCount(newRetryCount);

      // Agendar retry se não excedeu o máximo
      if (newRetryCount <= MAX_RETRY_ATTEMPTS && isMountedRef.current) {
        console.log(`🔄 Scheduling retry ${newRetryCount}/${MAX_RETRY_ATTEMPTS} in ${RETRY_DELAY * newRetryCount}ms`);
        setTimeout(
          () => {
            if (isMountedRef.current) {
              checkInterval();
            }
          },
          RETRY_DELAY * newRetryCount,
        ); // Exponential backoff
      } else {
        console.error('❌ Max retry attempts reached, giving up');
      }
    } finally {
      if (isMountedRef.current) {
        setIsChecking(false);
        // Limpar timeout de segurança
        if (checkingTimeoutRef.current) {
          clearTimeout(checkingTimeoutRef.current);
          checkingTimeoutRef.current = null;
        }
      }
    }
  }, [enabled, establishmentId, isQueryLoading, refetch, retryCount, onError]);

  /**
   * Força uma verificação imediata
   */
  const forceCheck = useCallback(() => {
    console.log('🔄 Force check triggered');
    setRetryCount(0); // Reset retry count for manual checks
    checkInterval();
  }, [checkInterval]);

  /**
   * Reinicia o gerenciador de intervalos
   */
  const restart = useCallback(() => {
    console.log('🔄 Restarting interval manager');

    // Limpar timer existente
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Resetar estados
    setCurrentInterval(null);
    setError(null);
    setLastChecked(null);
    setRetryCount(0);
    lastIntervalIdRef.current = null;
    lastCheckTimeRef.current = 0; // Reset timestamp de controle

    // Não iniciar verificação aqui - deixar o useEffect principal cuidar disso
    // para evitar duplicação na inicialização
    console.log('🔄 Restart completed - main useEffect will handle initial check');
  }, [enabled, establishmentId]);

  // Configurar verificação periódica
  useEffect(() => {
    if (!enabled || !establishmentId) {
      console.log('🚫 Interval checking disabled:', { enabled, establishmentId });
      return;
    }

    console.log('⏰ Setting up interval checking:', {
      establishmentId,
      checkInterval: CHECK_INTERVAL / 1000,
      enabled,
      timestamp: new Date().toISOString(),
    });

    // Verificação inicial
    console.log('🚀 Starting initial interval check...');
    checkInterval();

    // Configurar verificação periódica
    intervalRef.current = setInterval(() => {
      console.log(`⏰ Scheduled interval check (${CHECK_INTERVAL / 1000}s interval):`, {
        establishmentId,
        timestamp: new Date().toISOString(),
      });
      checkInterval();
    }, CHECK_INTERVAL);

    // Cleanup
    return () => {
      if (intervalRef.current) {
        console.log('🧹 Cleaning up interval checking timer');
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, establishmentId, checkInterval]);

  // Processar dados quando chegarem da query
  useEffect(() => {
    if (intervalsData) {
      console.log('🚀 Dados recebidos da query /current:', intervalsData);

      // SALVAMENTO IMEDIATO - antes de qualquer processamento
      saveImmediatelyFromAPIResponse(intervalsData, establishmentId);

      // Depois processar normalmente
      processIntervalData(intervalsData);
    } else if (queryError) {
      // Tratar erro da query
      const errorObj = queryError instanceof Error ? queryError : new Error('Erro na query de intervalos');
      setError(errorObj);

      if (onError) {
        onError(errorObj);
      }
    }
  }, [intervalsData, queryError, processIntervalData, onError]);

  // Cleanup ao desmontar
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (checkingTimeoutRef.current) {
        clearTimeout(checkingTimeoutRef.current);
        checkingTimeoutRef.current = null;
      }
    };
  }, []);

  // Logging para debug
  useEffect(() => {
    console.log('🔍 Interval Manager State:', {
      establishmentId,
      enabled,
      currentIntervalId: currentInterval?.id,
      currentIntervalName: currentInterval?.name,
      isChecking,
      hasError: !!error,
      lastChecked,
      retryCount,
      // Verificar configuração atual no localStorage
      localStorageConfig: (() => {
        try {
          const stored = localStorage.getItem('fila_digital_config');
          return stored ? JSON.parse(stored) : null;
        } catch {
          return null;
        }
      })(),
    });
  }, [currentInterval, isChecking, error, lastChecked, retryCount, establishmentId, enabled]);

  // Removido useEffect problemático que estava causando loop infinito
  // O processamento correto dos dados já é feito no useEffect abaixo (linha 357)

  return {
    currentInterval,
    isChecking: isChecking || isQueryLoading,
    error,
    lastChecked,
    forceCheck,
    restart,
  };
}

export default useIntervalManager;
